/**
 * PWA — nối trình duyệt với app (03/10/2026): đăng ký service worker, bắt lời mời cài của trình duyệt,
 * báo khi có bản mới, và tự tải lại khi mã của một màn hình không còn trên máy chủ. Luật tính toán
 * nằm ở `domain/pwa.ts`; giao diện (lời mời cài, hướng dẫn iPhone, thông báo bản mới) ở
 * `components/InstallApp.tsx`.
 */
import { create } from 'zustand';
import {
  installMode,
  shouldCheckForUpdate,
  shouldReloadAfterChunkError,
  type InstallMode,
} from '@/domain/pwa';

/** Sự kiện Chrome bắn ra khi trang đủ điều kiện cài — chưa có trong bộ kiểu DOM chuẩn. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PwaState {
  mode: InstallMode;
  /** Service worker của bản MỚI đã vào việc trong lúc trang đang mở — nên tải lại để chạy mã mới. */
  updateReady: boolean;
  /** Hộp hướng dẫn cài trên iPhone đang mở. */
  iosHelpOpen: boolean;
  /** Bấm «Cài ứng dụng»: mở hộp cài của trình duyệt, hoặc mở hướng dẫn trên iPhone. */
  requestInstall(): Promise<void>;
  closeIosHelp(): void;
}

const RELOAD_KEY = 'halo-chunk-reload-at';
/** Lời mời cài trình duyệt đưa sẵn; dùng một lần rồi bỏ. */
let deferredPrompt: BeforeInstallPromptEvent | null = null;

export const isStandaloneDisplay = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as { standalone?: boolean }).standalone === true;

const currentMode = (): InstallMode =>
  installMode({
    standalone: isStandaloneDisplay(),
    hasPrompt: deferredPrompt !== null,
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
  });

export const usePwaStore = create<PwaState>((set, get) => ({
  mode: currentMode(),
  updateReady: false,
  iosHelpOpen: false,
  async requestInstall() {
    const prompt = deferredPrompt;
    if (prompt) {
      // Mỗi lời mời chỉ mở được một lần — người dùng từ chối thì trình duyệt sẽ đưa lời mời khác sau.
      deferredPrompt = null;
      await prompt.prompt();
      await prompt.userChoice.catch(() => undefined);
      set({ mode: currentMode() });
      return;
    }
    if (get().mode === 'ios') set({ iosHelpOpen: true });
  },
  closeIosHelp: () => set({ iosHelpOpen: false }),
}));

function readLastReload(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_KEY) ?? 0) || 0;
  } catch {
    return 0;
  }
}

/**
 * Mã của từng màn hình tải theo nhu cầu, tên file mang hash của bản dựng. Triển khai bản mới xong thì
 * file của bản cũ không còn: máy đang mở bản cũ bấm sang màn chưa từng mở sẽ nhận 404. Tải lại trang
 * là lấy được bản mới — điện thoại chạy như ứng dụng (không có nút tải lại) thì càng cần tự làm việc này.
 */
function reloadOnMissingChunk(): void {
  window.addEventListener('vite:preloadError', (event) => {
    const now = Date.now();
    if (!shouldReloadAfterChunkError(readLastReload(), now)) return;
    event.preventDefault();
    try {
      sessionStorage.setItem(RELOAD_KEY, String(now));
    } catch {
      /* không ghi nhớ được thì vẫn tải lại — lần sau `readLastReload` trả 0 */
    }
    window.location.reload();
  });
}

function registerServiceWorker(): void {
  // Chỉ ở bản dựng thật: lúc phát triển, kho đệm của service worker sẽ giữ mã cũ và đánh lừa người viết.
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  // Trang mở ra đã có service worker điều khiển → lần đổi kế tiếp là do BẢN MỚI vừa triển khai. Lần
  // cài đầu tiên cũng bắn sự kiện này, nhưng lúc đó chưa có gì cũ để phải tải lại.
  const hadController = navigator.serviceWorker.controller !== null;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hadController) usePwaStore.setState({ updateReady: true });
  });

  const register = () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        // Ứng dụng trên điện thoại có thể mở suốt nhiều ngày: mỗi lần quay lại, cách lần hỏi trước đủ lâu
        // thì hỏi máy chủ xem có bản mới chưa.
        let lastCheckedAt = Date.now();
        document.addEventListener('visibilitychange', () => {
          const now = Date.now();
          if (document.visibilityState !== 'visible') return;
          if (!shouldCheckForUpdate(lastCheckedAt, now)) return;
          lastCheckedAt = now;
          registration.update().catch(() => undefined);
        });
      })
      // Đăng ký hỏng (trình duyệt chặn, chế độ ẩn danh…) thì app vẫn chạy như một trang web thường.
      .catch(() => undefined);
  };
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}

/** Gọi MỘT lần lúc khởi động (`main.tsx`), trước khi React vẽ: lời mời cài chỉ bắn một lần, rất sớm. */
export function startPwa(): void {
  const refreshMode = () => usePwaStore.setState({ mode: currentMode() });
  window.addEventListener('beforeinstallprompt', (event) => {
    // Không để trình duyệt tự bật thanh mời cài của nó: app tự mời, đúng chỗ, đúng lúc.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    refreshMode();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    refreshMode();
  });
  window.matchMedia('(display-mode: standalone)').addEventListener('change', refreshMode);
  reloadOnMissingChunk();
  registerServiceWorker();
}
